import React, { useState } from "react";
import { supabase } from "./supabase";
import { logSupabaseError } from "./utils";
import "./App.css";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const textareaStyle = {
  width: "100%",
  padding: "10px 14px",
  background: "var(--elevated)",
  border: "1px solid var(--border)",
  borderRadius: "var(--radius)",
  color: "var(--text)",
  fontFamily: "var(--font)",
  fontSize: "14px",
  outline: "none",
  resize: "vertical",
  boxSizing: "border-box",
};

// onOpenPrivacy/onOpenTerms open the URL-backed legal pages (App.js), which
// render as an overlay above this form, so nothing typed here is lost.
export default function WaitlistForm({ onClose, onOpenPrivacy, onOpenTerms }) {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    store_name: "",
    message: "",
  });
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [duplicate, setDuplicate] = useState(false);
  const [agreed, setAgreed] = useState(false);

  function set(key, value) {
    setForm((prev) => ({ ...prev, [key]: value }));
    setError(null);
  }

  async function handleSubmit() {
    if (!form.name.trim()) {
      setError("Please enter your name.");
      return;
    }
    if (!form.email.trim() || !EMAIL_RE.test(form.email.trim())) {
      setError("Please enter a valid email address.");
      return;
    }
    if (!agreed) {
      setError("Please agree to the Privacy Policy and Terms & Conditions.");
      return;
    }

    setLoading(true);
    const email = form.email.trim().toLowerCase();

    // Friendly pre-check — best-effort only. If it errors (e.g. offline),
    // fall through to the insert below rather than blocking submission;
    // the insert's own 23505 handling is the real backstop for dupes.
    const { data: alreadyApplied, error: checkError } = await supabase.rpc(
      "check_waitlist_email_exists",
      { p_email: email },
    );
    logSupabaseError("WaitlistForm: check_waitlist_email_exists", checkError);

    if (alreadyApplied) {
      setLoading(false);
      setDuplicate(true);
      setSubmitted(true);
      return;
    }

    const { error: insertError } = await supabase
      .from("waitlist_applications")
      .insert({
        name: form.name.trim(),
        email,
        phone: form.phone.trim() || null,
        store_name: form.store_name.trim() || null,
        message: form.message.trim() || null,
      });

    setLoading(false);

    if (insertError) {
      if (insertError.code === "23505") {
        setDuplicate(true);
        setSubmitted(true);
        return;
      }
      setError("Something went wrong: " + insertError.message);
      return;
    }

    setSubmitted(true);
  }

  return (
    <div className="report-overlay">
      <div className="report-panel" style={{ maxWidth: "480px" }}>
        <div className="report-header">
          <div>
            <h2 className="report-title">🚀 Request Early Access</h2>
            <p className="report-date">
              {submitted
                ? duplicate
                  ? "Already on the list"
                  : "You're on the list"
                : "Tell us a bit about your shop"}
            </p>
          </div>
          <button className="report-close" onClick={onClose}>
            ✕
          </button>
        </div>

        <div style={{ padding: "20px" }}>
          {submitted ? (
            <div style={{ textAlign: "center", padding: "20px 0" }}>
              <p style={{ fontSize: "40px", marginBottom: "12px" }}>✅</p>
              <h3
                style={{
                  fontSize: "17px",
                  fontWeight: 700,
                  color: "var(--text)",
                  marginBottom: "8px",
                }}
              >
                {duplicate
                  ? "Looks like you've already applied"
                  : "Thanks for your interest!"}
              </h3>
              <p
                style={{
                  fontSize: "14px",
                  color: "var(--text3)",
                  lineHeight: 1.6,
                }}
              >
                {duplicate ? (
                  <>
                    We already have an application on file for{" "}
                    <strong style={{ color: "var(--text)" }}>
                      {form.email.trim()}
                    </strong>{" "}
                    — we'll be in touch if there's a fit.
                  </>
                ) : (
                  <>
                    We're inviting a small group of shops at a time — we'll
                    reach out at{" "}
                    <strong style={{ color: "var(--text)" }}>
                      {form.email.trim()}
                    </strong>{" "}
                    if there's a fit.
                  </>
                )}
              </p>
              <button
                className="auth-submit"
                style={{ marginTop: "20px" }}
                onClick={onClose}
              >
                Close
              </button>
            </div>
          ) : (
            <div className="auth-form">
              <p
                style={{
                  fontSize: "13px",
                  color: "var(--text3)",
                  lineHeight: 1.6,
                }}
              >
                SuqPOS is currently invite-only while we work closely with
                a small group of early shops. Leave your details and
                we'll reach out if there's a fit.
              </p>

              {error && <div className="auth-error">⚠ {error}</div>}

              <div className="auth-field">
                <label>Your Name *</label>
                <input
                  placeholder="e.g. Tigist Haile"
                  value={form.name}
                  onChange={(e) => set("name", e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
                />
              </div>
              <div className="auth-field">
                <label>Email Address *</label>
                <input
                  type="email"
                  placeholder="you@example.com"
                  value={form.email}
                  onChange={(e) => set("email", e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
                />
              </div>
              <div className="auth-field">
                <label>Phone (optional)</label>
                <input
                  placeholder="+251 9xx xxx xxx"
                  value={form.phone}
                  onChange={(e) => set("phone", e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
                />
              </div>
              <div className="auth-field">
                <label>Store Name (optional)</label>
                <input
                  placeholder="e.g. Guadaye Mini Market"
                  value={form.store_name}
                  onChange={(e) => set("store_name", e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
                />
              </div>
              <div className="auth-field">
                <label>Tell us a bit about your shop (optional)</label>
                <textarea
                  rows={3}
                  placeholder="What are you hoping SuqPOS helps with?"
                  value={form.message}
                  onChange={(e) => set("message", e.target.value)}
                  style={textareaStyle}
                />
              </div>

              <label className="waitlist-consent">
                <input
                  type="checkbox"
                  checked={agreed}
                  onChange={(e) => setAgreed(e.target.checked)}
                />
                <span>
                  I agree to the{" "}
                  <button
                    type="button"
                    className="waitlist-consent-link"
                    onClick={onOpenPrivacy}
                  >
                    Privacy Policy
                  </button>{" "}
                  and{" "}
                  <button
                    type="button"
                    className="waitlist-consent-link"
                    onClick={onOpenTerms}
                  >
                    Terms &amp; Conditions
                  </button>
                </span>
              </label>

              <button
                className="auth-submit"
                onClick={handleSubmit}
                disabled={loading || !agreed}
              >
                {loading ? "Submitting..." : "Request Access →"}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
